import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { useCampaignVisitEfficiency } from '@/api/campaign';
import { TRAFFIC_TYPES } from '@/api/common';
import Flare, { Donut, Legend, Tooltip } from '@/components/Flare';
import { getPeriodTime, usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import {
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';
import { COLOR_WHITE } from '@/styles/palette';

type Props = {
  campaignIds?: string[];
  isLoading?: boolean;
};

const CampaignVisitEfficiencyChart = ({ campaignIds, isLoading: isCampaignLoading }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const {
    efficiencyData,
    isLoading: isEfficiencyLoading,
    error,
  } = useCampaignVisitEfficiency(campaignIds, startDate, endDate, audience?.id);
  const [disabledSeries, setDisabledSeries] = useState<string[] | undefined>([
    TRAFFIC_TYPES.UNRESOLVED,
  ]);

  const isLoading = isCampaignLoading || isEfficiencyLoading;
  const titleValue = audience?.isTAM ? t`TAM<br/>Visits` : t`Target Audience<br/>Visits`;

  const totalEnabledVisits = useMemo(() => {
    let total = 0;

    if (efficiencyData) {
      if (!disabledSeries?.includes(TRAFFIC_TYPES.TARGET)) {
        total += efficiencyData.targetVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.OTHER)) {
        total += efficiencyData.otherVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.BOT)) {
        total += efficiencyData.botVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED)) {
        total += efficiencyData.unresolvedVisits;
      }
    }
    return total;
  }, [efficiencyData, disabledSeries]);

  const seriesData = useMemo(() => {
    if (!efficiencyData) {
      return undefined;
    }

    return [
      {
        id: 'visits',
        name: t`Visits`,
        data: [
          {
            id: TRAFFIC_TYPES.TARGET,
            value: efficiencyData.targetVisits,
            name: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
            color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
            custom: { secondary: efficiencyData.targetVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.OTHER,
            value: efficiencyData.otherVisits,
            name: t`Other Companies`,
            color: VISITS_PRIMARY,
            custom: { secondary: efficiencyData.otherVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.BOT,
            value: efficiencyData.botVisits,
            name: t`Bot`,
            color: BOT_PRIMARY,
            custom: { secondary: efficiencyData.botVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.UNRESOLVED,
            value: efficiencyData.unresolvedVisits,
            name: t`Unknown`,
            color: UNKNOWN_PRIMARY,
            custom: {
              secondary: efficiencyData.unresolvedVisits / totalEnabledVisits,
            },
          },
        ],
      },
    ];
  }, [efficiencyData, audience?.isTAM, audience?.name, totalEnabledVisits]);

  return (
    <Panel title={<Trans>Visit Efficiency</Trans>} style={{ height: 483 }} noPadding>
      <Flare
        data={seriesData}
        title={`<b>${numberFormat(efficiencyData?.targetVisits, { compact: true })}</b><br />${titleValue}`}
        width="auto"
        height="auto"
        colors={seriesData ? seriesData?.[0].data.map((d) => d.color) : []}
        noPadding
        isLoading={isLoading}
        error={error}
        disabledSeriesIds={disabledSeries}
        onDisabledSeriesChange={setDisabledSeries}
      >
        <Legend showByCategory />
        <Donut
          y="value"
          noBorder
          noHoverEffect
          dataLabelColor={COLOR_WHITE}
          dataLabelFormat={(label) => {
            if (label.custom?.secondary && label.custom.secondary >= 0.02) {
              return numberFormat(label.custom?.secondary, { isPercent: true, precision: 0 });
            }
            return '';
          }}
          hideConnector
        />
        <Tooltip
          titleFormat={() => t`${getPeriodTime(startDate, endDate)} (Visits)`}
          rowLabelFormat={(item) => item?.key}
          rowValueFormat={(item) => {
            return numberFormat(item?.y);
          }}
          rowSecondaryValueFormat={(item) => {
            if (item?.custom?.secondary && item.custom.secondary > 0) {
              return numberFormat(item?.custom.secondary, { isPercent: true, precision: 1 });
            }
          }}
          rowValueLink={(point) => {
            if (point?.id === TRAFFIC_TYPES.TARGET) {
              return ROUTES.webTrafficActivityWithParams({
                'w.campaignIds': campaignIds,
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'true' : undefined,
                'w.metrics.visits': '[1,]',
              });
            }
            if (point?.id === TRAFFIC_TYPES.OTHER) {
              return ROUTES.webTrafficActivityWithParams({
                'w.campaignIds': campaignIds,
                'w.audienceIds!': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'false' : undefined,
                'w.metrics.visits': '[1,]',
              });
            }
            if (point?.key === 'total') {
              return ROUTES.webTrafficActivityWithParams({
                'w.campaignIds': campaignIds,
                'w.metrics.visits': '[1,]',
              });
            }
          }}
          showTotalRow
          unstable_hasRouterContext={false}
          shared
        />
      </Flare>
    </Panel>
  );
};

export default CampaignVisitEfficiencyChart;
